import React, { useState, useEffect, useRef } from 'react'
import { useCandidateChat } from '../CandidateChatContext'
import { useIsMobile, useMediaQuery } from '../../../../hooks/useMediaQuery'
import { useTranslation } from '../../../../hooks/useTranslation'
import ChatHeader from '../ChatHeader'
import TextChatWrap, { WIDE_CHAT_FRAME_MIN_PX } from './TextChatWrap'
import MediaWrap from './MediaWrap'
import LoadingStates from './LoadingStates'
import WaitingForCoach from './WaitingForCoach'
import ConnectionLost from './ConnectionLost'
import DegradedModeNotification from './DegradedModeNotification'
import { getEffectiveExitUrl } from '../utils/getEffectiveExitUrl'
import { Modal } from '~/components/_layouts'
import { Button } from '~/components/_atoms'

const Chat = () => {
  const {
    selectedCoach,
    candidateChatManager,
    connectionState,
    getBackgroundClass,
    getDarkModeClasses,
    afterChatPath,
  } = useCandidateChat()
  const isMobile = useIsMobile()
  const isWideFrame = useMediaQuery(`(min-width: ${WIDE_CHAT_FRAME_MIN_PX}px)`)
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)
  const [degradedModeNotification, setDegradedModeNotification] = useState(null)
  const textDisabledSentRef = useRef(null) // 'disabled' | 'enabled' | null
  const { t } = useTranslation('coach_chat')

  // Get current connection state from context (reactive) - must be before any hook that uses chatStatus/chatMode
  const {
    chatStatus,
    messages,
    chatMode,
    hasLocalVideo,
    hasLocalAudio,
    hasRemoteVideo,
    hasRemoteAudio,
    connectionLost = false,
    deloitteUser = false,
    disconnectionReason = null,
  } = connectionState

  // Notify coach when text chat is hidden/shown (e.g. mobile video: text hidden → send text_disabled)
  useEffect(() => {
    if (!candidateChatManager) return
    if (chatStatus !== 'chat_active') {
      textDisabledSentRef.current = null
      return
    }
    const textHidden = !(
      chatMode === 'text' ||
      chatMode === 'audio' ||
      !isMobile
    )
    if (textHidden && textDisabledSentRef.current !== 'disabled') {
      candidateChatManager.sendAction({ action_type: 'text_disabled' })
      textDisabledSentRef.current = 'disabled'
    } else if (!textHidden && textDisabledSentRef.current === 'disabled') {
      candidateChatManager.sendAction({ action_type: 'text_enabled' })
      textDisabledSentRef.current = 'enabled'
    } else if (!textHidden) {
      textDisabledSentRef.current = null
    }
  }, [candidateChatManager, chatStatus, chatMode, isMobile])

  // Listen for degraded mode events
  useEffect(() => {
    const handleDegradedMode = (data) => {
      if (data.isDegradedMode && data.message) {
        setDegradedModeNotification({
          message: data.message,
          requestedCallType: data.requestedCallType,
          actualCallType: data.actualCallType,
        })
      }
    }

    if (candidateChatManager) {
      candidateChatManager.addEventListener(
        'mediaDegradedMode',
        handleDegradedMode,
      )
    }

    return () => {
      if (candidateChatManager) {
        candidateChatManager.removeEventListener(
          'mediaDegradedMode',
          handleDegradedMode,
        )
      }
    }
  }, [candidateChatManager])

  // Ensure candidateChatManager is available before rendering (after hooks that need connectionState)
  if (!candidateChatManager) {
    console.error('Chat component: candidateChatManager is not available')
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('error.chat_manager_unavailable', {
              defaultValue: 'Chat Manager Unavailable',
            })}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('error.chat_manager_unavailable_description', {
              defaultValue:
                'Unable to initialize chat. Please refresh the page.',
            })}
          </p>
        </div>
      </div>
    )
  }

  // Show waiting for coach state when waiting for coach to accept
  const showWaitingForCoach = chatStatus === 'waiting_for_coach'

  // Show connection lost state when connection is lost
  const showConnectionLost = connectionLost

  // Determine if we should show video/audio components - only when chat is active
  const showVideoAudio =
    chatStatus === 'chat_active' &&
    (chatMode === 'video' || chatMode === 'audio')

  // Show text chat only when chat is active and on desktop device even during video/audio calls, but hide on mobile devices during video calls only
  const showTextChat =
    chatStatus === 'chat_active' &&
    (chatMode === 'text' || chatMode === 'audio' || !isMobile)

  // Handle close chat confirmation
  const handleCloseChat = () => {
    setShowCloseConfirmation(true)
  }

  const handleConfirmClose = () => {
    if (candidateChatManager.endChat) {
      candidateChatManager.endChat()
    } else {
      console.warn('Chat - candidateChatManager.endChat is not available')
    }
    setShowCloseConfirmation(false)
  }

  const handleCancelClose = () => {
    setShowCloseConfirmation(false)
  }

  const handleDismissDegradedMode = () => {
    setDegradedModeNotification(null)
  }

  /* const handleRetryConnection = () => {
    // Attempt to reconnect
    if (candidateChatManager && candidateChatManager.rtcService) {
      // console.log('Chat - Attempting to reconnect...')
      // This would trigger a reconnection attempt
      // For now, we'll just log it
      // console.log('Chat - Retry connection requested')
    }
  }

  const handleEndChatFromConnectionLost = () => {
    // End the chat when connection is lost
    if (candidateChatManager && candidateChatManager.endChat) {
      // console.log('Chat - Ending chat due to connection loss')
      candidateChatManager.endChat()
    }
  } */

  // Wide media+text: place media and text side-by-side so messages stay visible
  // after send when the chat panel is expanded (CC-UX-022).
  const useWideMediaTextLayout =
    isWideFrame && showVideoAudio && showTextChat && !showConnectionLost

  return (
    <>
      {/* Degraded mode notification */}
      {degradedModeNotification && (
        <DegradedModeNotification
          message={degradedModeNotification.message}
          onDismiss={handleDismissDegradedMode}
        />
      )}

      <div
        className={`candidate-chat grid h-full w-full ${getBackgroundClass()} ${chatMode} ${isMobile ? 'is-mobile' : ''} ${
          useWideMediaTextLayout
            ? 'grid-cols-[minmax(0,1fr)_minmax(17.5rem,1fr)] grid-rows-[auto_minmax(0,1fr)]'
            : 'grid-rows-[auto_auto_1fr]'
        }`}
      >
        {/* Chat header with controls */}
        <div
          className={
            useWideMediaTextLayout ? 'col-span-2 row-start-1' : undefined
          }
        >
          <ChatHeader
            selectedCoach={selectedCoach}
            chatMode={chatMode}
            chatStatus={chatStatus}
            onEndChat={handleCloseChat}
          />
        </div>

        {/* Content/Media area */}
        <div
          className={`min-h-0 ${
            useWideMediaTextLayout
              ? `col-start-1 row-start-2 overflow-hidden border-r border-solid ${getDarkModeClasses('border-gray-300', 'border-gray-600')}`
              : ''
          }`}
        >
          {/* Connection lost state - exit-only: OK button navigates out of frame (like skip survey) */}
          {showConnectionLost && (
            <ConnectionLost
              deloitteUser={deloitteUser}
              disconnectionReason={disconnectionReason}
              exitUrl={getEffectiveExitUrl(afterChatPath)}
            />
          )}

          {/* Waiting for coach state */}
          {showWaitingForCoach && <WaitingForCoach />}

          {/* Loading states for video/audio - only when chat is active */}
          {chatStatus === 'chat_active' && !showConnectionLost && (
            <LoadingStates
              chatMode={chatMode}
              chatStatus={chatStatus}
              hasLocalVideo={hasLocalVideo}
              hasLocalAudio={hasLocalAudio}
              hasRemoteVideo={hasRemoteVideo}
              hasRemoteAudio={hasRemoteAudio}
            />
          )}

          {/* Video/audio components */}
          {showVideoAudio && !showConnectionLost && (
            <MediaWrap
              chatMode={chatMode}
              chatManager={candidateChatManager}
              connectionState={connectionState}
              hasLocalVideo={hasLocalVideo}
              hasLocalAudio={hasLocalAudio}
              hasRemoteVideo={hasRemoteVideo}
              hasRemoteAudio={hasRemoteAudio}
              selectedCoach={selectedCoach}
              bgEffectsEnabled={false}
            />
          )}
        </div>

        {/* Text chat interface */}
        {showTextChat && (
          <div
            className={`min-h-0 ${
              useWideMediaTextLayout ? 'col-start-2 row-start-2' : ''
            }`}
          >
            <TextChatWrap
              messages={messages}
              chatStatus={chatStatus}
              selectedCoach={selectedCoach}
              wideFrameLayout={isWideFrame}
            />
          </div>
        )}

        {/* Close chat confirmation modal */}
        <Modal
          open={showCloseConfirmation}
          onClose={handleCancelClose}
          title={t('close_confirmation.title')}
          size="md"
          variant="secondary"
          actions={
            // Largest action label ~215px; 215*2 + gap-2 (8px) = 438px content.
            // Paper is @container/yns-modal; actions pad ~32px (px-4) → 470px.
            <div className="grid w-full min-w-0 grid-cols-1 gap-2 @[470px]/yns-modal:grid-cols-2">
              <Button
                key="cancel"
                color="secondary"
                onClick={handleCancelClose}
                variant="outline"
                className="w-full min-w-0"
              >
                {t('close_confirmation.cancel_button')}
              </Button>
              <Button
                key="confirm"
                onClick={handleConfirmClose}
                variant="solid"
                color="secondary"
                className="w-full min-w-0"
              >
                {t('close_confirmation.confirm_button')}
              </Button>
            </div>
          }
        >
          <div>{t('close_confirmation.description')}</div>
        </Modal>
      </div>
    </>
  )
}

export default Chat
